import React from "react";
import PropTypes from "prop-types";
import { useMemo } from "react";

const CURRENCY_SYMBOL_MAP = {
  "HK$": "HKD",
  "SG$": "SGD",
  "S$":  "SGD",
  "£":   "GBP",
  "€":   "EUR",
  "¥":   "CNY",
  "$":   "USD",
};

function detectCurrency(salary_package) {
  if (!salary_package) return null;
  for (const [symbol, code] of Object.entries(CURRENCY_SYMBOL_MAP)) {
    if (salary_package.includes(symbol)) return code;
  }
  return null;
}

function parseSalaryPackage(salary_package) {
  const matches = salary_package?.match(/[\d,]+(\.\d+)?/g);
  if (!matches || matches.length === 0) return {};
  const nums = matches.map((m) => parseFloat(m.replace(/,/g, "")));
  if (nums.length >= 2) return { minValue: nums[0], maxValue: nums[1] };
  return { value: nums[0] };
}

export default function GoogleJobsSchema({
  employment_type,
  job,
  job_currency,
  job_payment_type,
  site_name,
  site_domain_link,
  site_google_logo_link,
}) {

  const isInternal = useMemo(() => {
    const sectorCategory = job?.categories?.find(cat => cat.name === "Sector");
    const sectorValue = sectorCategory?.values?.[0]?.name?.toLowerCase();
    return sectorValue === "internal";
  }, [job?.categories]);


  const normalizedAddress = useMemo(() => {
    const p = job?.derived_info?.locations?.[0]?.postal_address;
    const locality = p?.locality || job?.location || "Not Specified";
  
    return {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressLocality: locality,
        addressCountry:
          p?.region_code === "UK"
            ? "United Kingdom"
            : p?.region_code || "Not Specified",
      },
    };
  }, [job]);

  const schemaData = {
    "@context": "https://schema.org/",
    "@type": "JobPosting",
    title: `${job.title}` ,
    description: `${job.description}`,
    url: `${site_domain_link}${isInternal ? '/join-us' : ''}/jobs/${job.url_slug}`,

    identifier: {
      "@type": "PropertyValue",
      name: `${site_name}`,
      value: `${job.external_reference}`,
    },
    datePosted: `${job.published_at || job.created_at}`,
    validThrough: `${job.expires_at}`,
    hiringOrganization: {
      "@type": "Organization",
      name: `${site_name}`,
      sameAs: `${site_domain_link}`,
      logo: `${site_google_logo_link}`,
    },
    employmentType: `${employment_type}`,
   jobLocation: normalizedAddress,
    baseSalary: {
      "@type": "MonetaryAmount",
      currency: detectCurrency(job.salary_package) || job_currency,
      value: {
        "@type": "QuantitativeValue",
        ...(parseFloat(job.salary_low) > 0 && parseFloat(job.salary_high) > 0
          ? { minValue: parseFloat(job.salary_low), maxValue: parseFloat(job.salary_high) }
          : parseSalaryPackage(job.salary_package)),
        unitText: `${job_payment_type}`,
      },
    },
  };

  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />;
}

GoogleJobsSchema.defaultProps = {
  job_currency: "GBP",
  job_payment_type: "YEAR",
};

GoogleJobsSchema.propTypes = {
  jobs: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    external_reference: PropTypes.string,
    created_at: PropTypes.string,
    expires_at: PropTypes.string,
    location: PropTypes.string,
    salary_package: PropTypes.string,
  }),
  employment_type: PropTypes.oneOf([
    "FULL_TIME",
    "PART_TIME",
    "CONTRACTOR",
    "TEMPORARY",
    "INTERN",
    "VOLUNTEER",
    "PER_DIEM",
    "OTHER",
  ]),
  job_currency: PropTypes.string,
  job_payment_type: PropTypes.string,
  site_name: PropTypes.string,
  site_domain_link: PropTypes.string,
  site_google_logo_link: PropTypes.string,
};
